/**
 * paises.ts — Catálogo de países soportados para el teléfono del app.
 *
 * Neocoop opera en Guatemala pero tiene clientes en la diáspora
 * (USA principalmente) y en la región centroamericana. Este catálogo
 * es la fuente de verdad para el selector de país + prefijo en todos
 * los inputs de teléfono del admin (ficha del asociado, migración
 * COBASY, wizard de inscripción, etc.).
 *
 * El E.164 resultante (`+{prefijoDigits}{numero}`) determina también
 * el email de login del app: `{sin_prefijo_+}@neocoop.app`. Cambios
 * en este catálogo deben ir en sincronía con la validación de la RPC
 * `actualizar_telefono_asociado_admin` (migración 0111).
 */

export type Pais = {
  iso: string
  nombre: string
  prefijoDigits: string // sin el "+"
  bandera: string
  minLen: number        // dígitos del número nacional (sin prefijo)
  maxLen: number
  placeholder: string
}

export const PAISES: Pais[] = [
  { iso: 'GT', nombre: 'Guatemala',      prefijoDigits: '502', bandera: '🇬🇹', minLen: 8,  maxLen: 8,  placeholder: '3002 8837' },
  { iso: 'US', nombre: 'Estados Unidos', prefijoDigits: '1',   bandera: '🇺🇸', minLen: 10, maxLen: 10, placeholder: '415 555 1234' },
  { iso: 'MX', nombre: 'México',         prefijoDigits: '52',  bandera: '🇲🇽', minLen: 10, maxLen: 10, placeholder: '55 1234 5678' },
  { iso: 'SV', nombre: 'El Salvador',    prefijoDigits: '503', bandera: '🇸🇻', minLen: 8,  maxLen: 8,  placeholder: '7012 3456' },
  { iso: 'HN', nombre: 'Honduras',       prefijoDigits: '504', bandera: '🇭🇳', minLen: 8,  maxLen: 8,  placeholder: '9876 5432' },
  { iso: 'CR', nombre: 'Costa Rica',     prefijoDigits: '506', bandera: '🇨🇷', minLen: 8,  maxLen: 8,  placeholder: '8765 4321' },
  { iso: 'PA', nombre: 'Panamá',         prefijoDigits: '507', bandera: '🇵🇦', minLen: 8,  maxLen: 8,  placeholder: '6123 4567' },
]

export const PAIS_DEFAULT: Pais = PAISES[0] // Guatemala

/**
 * Detecta el país a partir de un teléfono en formato E.164 (o cualquier
 * string con dígitos). Prueba los prefijos de mayor a menor largo para
 * no matchear "1" antes de "502".
 */
export function detectarPais(telefonoE164: string | null): Pais {
  if (!telefonoE164) return PAIS_DEFAULT
  const digits = telefonoE164.replace(/\D/g, '')
  const ordenados = [...PAISES].sort((a, b) => b.prefijoDigits.length - a.prefijoDigits.length)
  for (const p of ordenados) {
    if (digits.startsWith(p.prefijoDigits)) return p
  }
  return PAIS_DEFAULT
}

/**
 * Extrae los dígitos nacionales (sin prefijo) de un teléfono E.164
 * asumiendo que pertenece al país dado.
 */
export function digitosNacionales(telefonoE164: string | null, pais: Pais): string {
  if (!telefonoE164) return ''
  const digits = telefonoE164.replace(/\D/g, '')
  if (digits.startsWith(pais.prefijoDigits)) {
    return digits.slice(pais.prefijoDigits.length)
  }
  return ''
}

/**
 * Formatea un E.164 para display amigable en tablas y detalles.
 * Guatemala: +502 XXXX XXXX
 * USA/CA:    +1 XXX XXX XXXX
 * México:    +52 XX XXXX XXXX
 * Centroam:  +5XX XXXX XXXX
 */
export function formatearTelefonoDisplay(telefonoE164: string | null): string {
  if (!telefonoE164) return '—'
  const d = telefonoE164.replace(/\D/g, '')
  if (d.length === 11 && d.startsWith('502')) return `+502 ${d.slice(3, 7)} ${d.slice(7)}`
  if (d.length === 8) return `+502 ${d.slice(0, 4)} ${d.slice(4)}` // input local sin prefijo
  if (d.length === 11 && d.startsWith('1'))   return `+1 ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7)}`
  if (d.length === 12 && d.startsWith('52'))  return `+52 ${d.slice(2, 4)} ${d.slice(4, 8)} ${d.slice(8)}`
  if (d.length === 11 && /^(503|504|506|507)/.test(d)) {
    return `+${d.slice(0, 3)} ${d.slice(3, 7)} ${d.slice(7)}`
  }
  return telefonoE164
}

/** Valida un teléfono nacional (sin prefijo) para un país dado. */
export function validarNacional(nacional: string, pais: Pais): boolean {
  return /^\d+$/.test(nacional) && nacional.length >= pais.minLen && nacional.length <= pais.maxLen
}

/** Arma el E.164 a partir de país + nacional. Ej: GT + "30028837" → "+50230028837". */
export function armarE164(pais: Pais, nacional: string): string {
  return `+${pais.prefijoDigits}${nacional}`
}
